import { useState, useCallback, useEffect } from 'react';

export const useAccountLinking = () => {
  const [user, setUser] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [connectedWallets, setConnectedWallets] = useState(new Set());

  // Restore persisted user session
  useEffect(() => {
    try {
      const raw = localStorage.getItem('sw_user');
      console.log('💾 Loading persisted user from localStorage:', raw);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && (parsed.id || parsed.username)) {
          console.log('👤 Restoring user session:', parsed);
          setUser(parsed);
        } else {
          console.log('❌ Invalid user data in localStorage');
        }
      } else {
        console.log('📭 No user data in localStorage');
      }
    } catch (error) {
      console.error('❌ Error loading user from localStorage:', error);
    }
  }, []);

  // Logout function - defined early so it can be used in effects
  const logout = useCallback(() => {
    console.log('🚪 Logging out - clearing user data');
    setUser(null);
    setWallets([]);
    setError('');
    setConnectedWallets(new Set());
    localStorage.removeItem('sw_user');
    // Note: We don't clear JWT token here - that's handled by JWT context
  }, []);

  // Listen for wallet disconnection events
  useEffect(() => {
    const checkWalletConnection = () => {
      const phantom = window.phantom;
      if (!phantom) return;

      const newConnectedWallets = new Set();
      
      // Check each chain
      if (phantom.solana?.isConnected) {
        newConnectedWallets.add('solana');
      }
      if (phantom.ethereum?.selectedAddress) {
        newConnectedWallets.add('ethereum');
      }
      if (phantom.sui?.isConnected) {
        newConnectedWallets.add('sui');
      }

      // If all wallets disconnected, clear wallet data but preserve JWT auth
      if (newConnectedWallets.size === 0 && connectedWallets.size > 0) {
        console.log('🔌 All wallets disconnected - clearing wallet data (preserving JWT auth)');
        setUser(null);
        setWallets([]);
        setError('');
        setConnectedWallets(newConnectedWallets);
        localStorage.removeItem('sw_user');
        // Note: JWT token remains in localStorage for continued authentication
      }

      setConnectedWallets(newConnectedWallets);
    };

    // Check immediately
    checkWalletConnection();

    // Check periodically
    const interval = setInterval(checkWalletConnection, 1000);

    // Listen for Phantom disconnect events
    const phantom = window.phantom;
    if (phantom?.solana) {
      phantom.solana.on('disconnect', () => {
        console.log('🔌 Solana wallet disconnected');
        checkWalletConnection();
      });
    }

    return () => {
      clearInterval(interval);
    };
  }, [connectedWallets.size, logout]);

  // Persist user session
  useEffect(() => {
    console.log('💾 Persisting user to localStorage:', user);
    try {
      if (user) {
        localStorage.setItem('sw_user', JSON.stringify(user));
        console.log('✅ User persisted to localStorage');
      } else {
        localStorage.removeItem('sw_user');
        console.log('🗑️ User removed from localStorage');
      }
    } catch (error) {
      console.error('❌ Error persisting user to localStorage:', error);
    }
  }, [user]);


  const buildMessage = useCallback(({ action, nonce, user, domain, expires, chain }) => {
    return [
      'SoulWars Account Linking',
      `Action: ${action}`,
      `Nonce: ${nonce}`,
      `User: ${user || 'anonymous'}`,
      `Domain: ${domain}`,
      `Expires: ${expires}`,
      `Chain: ${chain}`,
    ].join('\n');
  }, []);

  const signMessage = useCallback(async (chain, message) => {
    const phantom = window.phantom;
    if (!phantom) {
      throw new Error('Phantom wallet not found');
    }

    switch (chain) {
      case 'solana':
        const solanaResponse = await phantom.solana.signMessage({
          message: new TextEncoder().encode(message)
        });
        // Import bs58 dynamically to avoid build issues
        const bs58 = await import('bs58');
        return bs58.default.encode(solanaResponse.signature);
      case 'ethereum':
      case 'polygon':
      case 'base':
        return await phantom.ethereum.request({
          method: 'personal_sign',
          params: [message, phantom.ethereum.selectedAddress]
        });
      case 'sui':
        const suiResponse = await phantom.sui.signMessage({
          message: new TextEncoder().encode(message)
        });
        return suiResponse.signature;
      default:
        throw new Error(`Unsupported chain: ${chain}`);
    }
  }, []);

  const getWalletAddress = useCallback(async (chain) => {
    const phantom = window.phantom;
    if (!phantom) {
      throw new Error('Phantom wallet not found');
    }

    switch (chain) {
      case 'solana':
        if (!phantom.solana?.isConnected) {
          await phantom.solana.connect();
        }
        return phantom.solana.publicKey?.toString();
      case 'ethereum':
      case 'polygon':
      case 'base':
        if (!phantom.ethereum?.selectedAddress) {
          await phantom.ethereum.request({ method: 'eth_requestAccounts' });
        }
        return phantom.ethereum.selectedAddress;
      case 'sui':
        if (!phantom.sui?.isConnected) {
          await phantom.sui.connect();
        }
        return phantom.sui.address;
      default:
        throw new Error(`Unsupported chain: ${chain}`);
    }
  }, []);

  const getNonce = useCallback(async (purpose, chain, userId = null) => {
    const response = await fetch('/api/auth/nonce', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ purpose, chain, userId })
    });
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to get nonce');
    }
    
    return data;
  }, []);

  const verifySignature = useCallback(async (chain, address, signature, nonce, username = null, action = 'login') => {
    const response = await fetch('/api/auth/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chain,
        address,
        signature,
        nonce,
        username,
        action
      })
    });
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Verification failed');
    }
    
    return data;
  }, []);

  const linkWallet = useCallback(async (chain, username = null) => {
    setLoading(true);
    setError('');

    try {
      // Get wallet address
      const address = await getWalletAddress(chain);
      if (!address) {
        throw new Error('Failed to get wallet address');
      }

      // Check if wallet is already linked
      const userResponse = await fetch(`/api/user-lookup/by-wallet/${chain}/${encodeURIComponent(address)}`);
      const userData = await userResponse.json();
      
      if (userData.success) {
        // Wallet is already linked
        setUser(userData.user);
        return { success: true, user: userData.user, isExisting: true };
      }

      // Get nonce for new account
      const nonceData = await getNonce('login', chain);
      
      // Build and sign message
      const message = buildMessage({
        action: 'login',
        nonce: nonceData.nonce,
        domain: window.location.host,
        expires: nonceData.expiresAt,
        chain
      });
      
      const signature = await signMessage(chain, message);

      // Verify and create account
      const verifyData = await verifySignature(chain, address, signature, nonceData.nonce, username, 'login');
      
      setUser({ id: verifyData.userId, username: username || `user_${verifyData.userId}` });
      return { success: true, user: { id: verifyData.userId, username: username || `user_${verifyData.userId}` }, isExisting: false };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [getWalletAddress, getNonce, buildMessage, signMessage, verifySignature]);

  const linkAdditionalWallet = useCallback(async (chain) => {
    if (!user) {
      throw new Error('No user logged in');
    }

    setLoading(true);
    setError('');

    try {
      // Get wallet address
      const address = await getWalletAddress(chain);
      if (!address) {
        throw new Error('Failed to get wallet address');
      }

      // Get nonce for linking
      const nonceData = await getNonce('link-wallet', chain, user.id);
      
      // Build and sign message
      const message = buildMessage({
        action: 'link-wallet',
        nonce: nonceData.nonce,
        user: String(user.id),
        domain: window.location.host,
        expires: nonceData.expiresAt,
        chain
      });
      
      const signature = await signMessage(chain, message);

      // Verify and link wallet
      const verifyData = await verifySignature(chain, address, signature, nonceData.nonce, null, 'link-wallet');
      
      // Reload wallets
      await loadWallets();
      
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [user, getWalletAddress, getNonce, buildMessage, signMessage, verifySignature]);

  const loadWallets = useCallback(async () => {
    if (!user?.id) return;

    try {
      const response = await fetch(`/api/user-lookup/${encodeURIComponent(user.id)}/wallets`);
      const data = await response.json();
      if (data.success) {
        setWallets(data.wallets);
      }
    } catch (err) {
      console.error('Failed to load wallets:', err);
    }
  }, [user]);

  // Auto-load wallets when user changes
  useEffect(() => {
    if (user?.id) {
      loadWallets();
    }
  }, [user?.id, loadWallets]);

  const getUserByWallet = useCallback(async (chain, address) => {
    try {
      const response = await fetch(`/api/user-lookup/by-wallet/${chain}/${encodeURIComponent(address)}`);
      const data = await response.json();
      return data.success ? data.user : null;
    } catch (err) {
      console.error('Failed to get user by wallet:', err);
      return null;
    }
  }, []);

  return {
    user,
    wallets,
    loading,
    error,
    linkWallet,
    linkAdditionalWallet,
    loadWallets,
    getUserByWallet,
    logout,
    setUser,
    setError
  };
};
